import type { UserRole } from '@compass/client/types/membership';
import { useStore } from '@store/index';
import { useMemo } from 'react';

import { useCurrentUserRoles } from './useUsers';

/**
 * Explicit role slugs whose holders oversee every case in the org and so should
 * land on the full case list (empty assignee filter) rather than the
 * "my cases + unassigned" default. Base slugs are sourced from
 * pkg/auth/role_templates.go; `supervisor-knipper-uat` is an org-specific custom
 * supervisor role. Add new supervisor variants here as they are created.
 */
const SEES_ALL_ORG_CASES_ROLE_SLUGS = [
  'supervisor',
  'supervisor-knipper-uat',
  'organization-admin',
] as const;

const ALL_CASES_SLUGS = new Set<string>(SEES_ALL_ORG_CASES_ROLE_SLUGS);

/**
 * Whether a role slug is one of the explicit {@link SEES_ALL_ORG_CASES_ROLE_SLUGS}.
 */
const grantsAllOrgCasesView = (slug?: string): boolean =>
  !!slug && ALL_CASES_SLUGS.has(slug.toLowerCase());

/**
 * Whether the current user holds a role that oversees all cases anywhere in the
 * selected organization (any supervisor role or organization-admin).
 *
 * Supervisor is a program-scoped role, and the org-wide Tier-2 Cases page has no
 * single selected program — so this checks every role the user holds in the
 * selected org (org-level AND any program-level role within it) plus global
 * roles, mirroring how {@link import('./useUserTier').useUserTier} resolves the
 * tier org-wide. Using the per-program `useCurrentUserContextRoles` here would
 * drop a supervisor role belonging to a non-active program.
 *
 * UI convenience only — authorization is always enforced server-side.
 */
export const useSeesAllOrgCases = (): boolean => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  // Same query useUserTier / useCurrentUserContextRoles run (shared cache key →
  // no extra fetch); returns the user's unfiltered roles.
  const { data } = useCurrentUserRoles(
    selectedOrganizationId ?? undefined,
    selectedProgramId ?? undefined,
  );

  return useMemo(() => {
    const inSelectedOrg = (role: UserRole): boolean =>
      (!!selectedOrganizationId && role.organization_id === selectedOrganizationId) ||
      (!role.organization_id && !role.program_id);
    return (data?.user_roles || []).some(
      role => inSelectedOrg(role) && grantsAllOrgCasesView(role.slug),
    );
  }, [data?.user_roles, selectedOrganizationId]);
};
